import React, { useState, useEffect, useRef } from "react";
import { playBeep } from "../../utils/audio";
import { Container, Box, Alert } from "@mui/material";
import PrintTypeSelector from "./components/PrintTypeSelector";
import NfeKeyInput from "./components/NfeKeyInput";
import PrinterStatusAlert from "./components/PrinterStatusAlert";
import UnifiedInvoiceDataDisplay from "./shared/components/InvoiceDataDisplay/UnifiedInvoiceDataDisplay";
import HelpButton from "../components/HelpButton";
import PrintControls from "./shared/components/PrintControls";
import NfeFormContainer from "./components/NfeFormContainer";
import NfeModals from "./components/NfeModals";
import NfeDetailsSkeleton from "./shared/components/NfeDetailsSkeleton";
import { usePrintType } from "./hooks/usePrintType";
import { useEanValidation } from "./hooks/useEanValidation";
import { usePrinterSelection } from "./hooks/usePrinterSelection";
import { useLabelPrint } from "./hooks/useLabelPrint";
import { StyledTextField, ActionButton } from "./styles/SharedStyles";
import {
  useGetMarketplaceInvoice,
  useGetMarketplaceInvoiceLabel,
  useMarkInvoiceAsPrinted,
} from "../../client/hooks/marketplace/marketplace.queries";
import useBridgeStatus from "./shared/hooks/useBridgeStatus";


const PrintNfeLabel = () => {
  const {
    printType,
    printTypeModalOpen,
    pendingPrintType,
    setPendingPrintType,
    handleSavePrintType,
    handleOpenPrintTypeModal,
  } = usePrintType();

  const PrintTypeModal = (
    <PrintTypeSelector
      open={printTypeModalOpen}
      pendingPrintType={pendingPrintType}
      setPendingPrintType={setPendingPrintType}
      onSave={handleSavePrintType}
    />
  );

  const [nfeKey, setNfeKey] = useState("");
  const [debouncedNfeKey, setDebouncedNfeKey] = useState("");
  const [invoiceData, setInvoiceData] = useState(null);
  const [labelFormat, setLabelFormat] = useState("zpl");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [markPrintedEnabled, setMarkPrintedEnabled] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNfeKey(nfeKey);
    }, 200);

    return () => clearTimeout(timer);
  }, [nfeKey]);

  const {
    data: invoice,
    isLoading: invoiceLoading,
    error: invoiceError,
  } = useGetMarketplaceInvoice(debouncedNfeKey, !!debouncedNfeKey && debouncedNfeKey.length === 44);

  const {
    isLoading: labelLoading,
    refetch: fetchLabel,
  } = useGetMarketplaceInvoiceLabel(debouncedNfeKey, labelFormat, !!debouncedNfeKey && debouncedNfeKey.length === 44);

  const { refetch: markOrderAsPrinted } = useMarkInvoiceAsPrinted(
    debouncedNfeKey, 
    markPrintedEnabled
  );

  const {
    isConnected: bridgeConnected,
    printers: availablePrinters,
    selectedPrinter,
    isLoading: loadingPrinters,
    downloadProgress,
    loadPrinters: detectPrinters,
    printLabel: printToBridge,
    selectPrinter,
    downloadBridgeInstaller,
  } = useBridgeStatus();

  const { eanInputs, eanValidated, handleEanInputChange, resetEans } = useEanValidation(invoiceData, printType);

  const {
    printerModalOpen,
    setPrinterModalOpen,
    bridgeInstallModalOpen,
    setBridgeInstallModalOpen,
    autoPrintEnabled,
    shouldAutoPrint,
    setShouldAutoPrint,
    handleSelectPrinter,
  } = usePrinterSelection(availablePrinters, selectedPrinter, selectPrinter);

  const {
    showForceDialog,
    labelError,
    handlePrintLabel,
    handleCloseForceDialog,
    handleConfirmForceDialog,
  } = useLabelPrint({
    printType,
    invoiceData,
    eanValidated,
    bridgeConnected,
    labelFormat,
    selectedPrinter,
    fetchLabel,
    printToBridge,
    setPrinterModalOpen,
    resetEans,
    inputRef,
    setNfeKey,
    onPrintSuccess: async () => {
      if (debouncedNfeKey) {
        try {
          setMarkPrintedEnabled(true);
          await markOrderAsPrinted();
          setMarkPrintedEnabled(false);
        } catch (error) {
          setMarkPrintedEnabled(false);
          console.error("Erro ao marcar nota como impressa:", error);
        }
      }
    },
  });

  const handleNfeKeyChange = (e) => {
    const newValue = e.target.value;
    if (
      newValue &&
      nfeKey &&
      e.target.selectionStart === e.target.selectionEnd
    ) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.select();
        }
      }, 0);
    }
    setNfeKey(newValue);
  };

  const handleSubmit = (e, scannedData = null) => {
    if (e) e.preventDefault();
    const keyToUse = scannedData || nfeKey;
    setNfeKey(keyToUse);
  };

  useEffect(() => {
    if (
      !printerModalOpen &&
      shouldAutoPrint &&
      invoiceData &&
      selectedPrinter &&
      bridgeConnected
    ) {
      setShouldAutoPrint(false);
      handlePrintLabel();
    }
  }, [printerModalOpen, shouldAutoPrint, invoiceData, selectedPrinter, bridgeConnected, setShouldAutoPrint]);

  const handleOpenScanner = () => setScannerOpen(true);
  const handleCloseScanner = () => setScannerOpen(false);
  const handleScanResult = (scannedData) => {
    setNfeKey(scannedData);
    setTimeout(() => {
      handleSubmit(null, scannedData);
    }, 500);
  };

  useEffect(() => {
    if (invoice) {
      setInvoiceData(invoice);
      // Se o checkbox de impressão automática estiver marcado, ativa o shouldAutoPrint
      if (autoPrintEnabled && selectedPrinter && bridgeConnected) {
        setShouldAutoPrint(true);
      }
    }
  }, [invoice, autoPrintEnabled, selectedPrinter, bridgeConnected, setShouldAutoPrint]);


  useEffect(() => {
    if (invoiceError) {
      playBeep(400, 0.4, "error");
    }
  }, [invoiceError]);

  useEffect(() => {
    if (printerModalOpen && availablePrinters.length === 0 && bridgeConnected) {
      detectPrinters();
    }
  }, [printerModalOpen, bridgeConnected, availablePrinters.length, detectPrinters]);

  const handlePrintButtonClick = () => {
    if (!selectedPrinter) {
      setShouldAutoPrint(true);
      setPrinterModalOpen(true);
    } else {
      handlePrintLabel();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        py: { xs: 1, md: 3 },
      }}
    >
      <Container
        component="main"
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <NfeFormContainer onOpenPrintTypeModal={handleOpenPrintTypeModal}>
          <NfeKeyInput
            nfeKey={nfeKey}
            onChange={handleNfeKeyChange}
            onSubmit={handleSubmit}
            inputRef={inputRef}
            invoiceLoading={invoiceLoading}
            onOpenScanner={handleOpenScanner}
            StyledTextField={StyledTextField}
            ActionButton={ActionButton}
          />

          {invoiceError && (
            <Alert
              severity="error"
              sx={{
                mt: 2,
                "& .MuiAlert-message": {
                  fontFamily:
                    '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                  fontSize: "0.95rem",
                  fontWeight: 400,
                  lineHeight: 1.5,
                },
              }}
            >
              Erro ao buscar dados da nota fiscal. Verifique a chave de acesso.
            </Alert>
          )}
        </NfeFormContainer>

        {invoiceLoading ? (
          <NfeDetailsSkeleton visible={true} />
        ) : (
          <UnifiedInvoiceDataDisplay
            invoiceData={invoiceData}
            visible={!!invoiceData}
            showEanField={printType === "detalhada"}
            eanInputs={eanInputs}
            onEanInputChange={handleEanInputChange}
            eanValidated={eanValidated}
          />
        )}
        
        {PrintTypeModal}

        {invoiceData && (
          <>
            {!bridgeConnected && labelFormat === "zpl" && (
              <PrinterStatusAlert
                onInstallBridge={() => setBridgeInstallModalOpen(true)}
              />
            )}

            <PrintControls
              visible={!!invoiceData}
              labelFormat={labelFormat}
              onFormatChange={setLabelFormat}
              selectedPrinter={selectedPrinter}
              onPrint={handlePrintButtonClick}
              onConfigurePrinter={() => setPrinterModalOpen(true)}
              labelLoading={labelLoading}
              labelError={labelError}
              bridgeConnected={bridgeConnected}
            />
          </>
        )}

        <NfeModals
          printerModalOpen={printerModalOpen}
          setPrinterModalOpen={setPrinterModalOpen}
          availablePrinters={availablePrinters}
          selectedPrinter={selectedPrinter}
          loadingPrinters={loadingPrinters}
          handleSelectPrinter={handleSelectPrinter}
          detectPrinters={detectPrinters}
          autoPrintEnabled={autoPrintEnabled}
          bridgeInstallModalOpen={bridgeInstallModalOpen}
          setBridgeInstallModalOpen={setBridgeInstallModalOpen}
          downloadProgress={downloadProgress}
          downloadBridgeInstaller={downloadBridgeInstaller}
          scannerOpen={scannerOpen}
          handleCloseScanner={handleCloseScanner}
          handleScanResult={handleScanResult}
          showForceDialog={showForceDialog}
          handleCloseForceDialog={handleCloseForceDialog}
          handleConfirmForceDialog={handleConfirmForceDialog}
        />
      </Container>

      <HelpButton context="etiquetas" />
    </Box>
  );
};

export default PrintNfeLabel;
